import { useEffect, useState } from "react";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FilePdf, X } from "@phosphor-icons/react";
import { fmtDateTime } from "@/lib/dates";

export default function RecordDetailDialog({ recordId, open, onClose }) {
  const { user } = useAuth();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !recordId) return;
    setLoading(true);
    api.get(`/records/${recordId}`)
      .then((r) => setRecord(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [open, recordId]);

  const downloadReceipt = async (copies = 1) => {
    try {
      const url = copies === 3 ? `/records/${recordId}/receipt.pdf?copies=3` : `/records/${recordId}/receipt.pdf`;
      const res = await api.get(url, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `receipt-${recordId.slice(0, 8)}${copies === 3 ? "-3ply" : ""}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const fmt = (v) => fmtDateTime(v);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl rounded-none border-neutral-900 max-h-[90vh] overflow-y-auto" data-testid="record-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">Record Detail</DialogTitle>
        </DialogHeader>
        {loading && <div className="p-6 text-center text-neutral-400 text-sm">Loading…</div>}
        {record && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <Field label="Issue No (IV)" value={record.receipt_no || "—"} mono />
              <Field label="Return No (RT)" value={record.return_receipt_no || "—"} mono />
              <Field label="Request (REQ)" value={record.request_no || "—"} mono />
              <Field label="Status" value={record.status.toUpperCase()} />
              <Field label="Engineer" value={record.engineer_name} />
              <Field label="Email" value={record.engineer_email || "—"} mono />
              <Field label="Region" value={record.region} />
              <Field label="Customer" value={record.customer_name || "—"} />
              <Field label="Work Order" value={record.work_order_id || "—"} mono />
              <Field label="Site" value={record.site || "—"} />
              <Field label="Taken at" value={fmt(record.taken_at)} mono />
              <Field label="Returned at" value={fmt(record.returned_at)} mono />
              <Field label="Issued by" value={record.created_by_name} />
            </div>

            <section>
              <div className="label-eyebrow mb-2">Parts Taken</div>
              <ul className="border border-neutral-200 divide-y divide-neutral-100">
                {record.parts_taken.map((p, i) => (
                  <li key={i} className="p-2 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">{p.name}</span>
                      <div className="flex items-center gap-2">
                        {p.chargeable && (
                          <span className="px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider bg-amber-100 text-amber-900">Chargeable</span>
                        )}
                        <span className={`px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider ${p.kind === "rb" ? "bg-amber-100 text-amber-800" : "bg-neutral-900 text-neutral-50"}`}>
                          {p.kind === "rb" ? "RB" : "NEW"}
                        </span>
                        <span className="text-neutral-500 font-mono">× {p.quantity}</span>
                      </div>
                    </div>
                    {p.serials && p.serials.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1" data-testid={`detail-part-${i}-serials`}>
                        <span className="text-[10px] font-mono uppercase tracking-wider text-neutral-500">S/N</span>
                        {p.serials.map((s, si) => (
                          <span key={si} className="px-1.5 py-0.5 text-[10px] font-mono bg-neutral-100 border border-neutral-300">{s}</span>
                        ))}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </section>

            {record.parts_returned?.length > 0 && (
              <section>
                <div className="label-eyebrow mb-2">Parts Returned</div>
                <ul className="border border-neutral-200 divide-y divide-neutral-100">
                  {record.parts_returned.map((p, i) => (
                    <li key={i} className="flex justify-between items-center p-2 text-sm">
                      <span className="font-medium">{p.name} <span className="text-neutral-500 font-mono">× {p.quantity}</span></span>
                      <div className="flex items-center gap-2">
                        <span className={`px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider ${p.kind === "rb" ? "bg-amber-100 text-amber-800" : "bg-neutral-900 text-neutral-50"}`}>
                          {p.kind === "rb" ? "RB" : "NEW"}
                        </span>
                        <span className={`px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider ${p.status === "used" ? "bg-red-100 text-red-800" : "bg-emerald-100 text-emerald-800"}`}>
                          {p.status}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {record.faulty_parts?.length > 0 && (
              <section data-testid="detail-faulty-parts">
                <div className="label-eyebrow mb-2 text-amber-800">Faulty parts brought back from site</div>
                <ul className="border border-amber-300 divide-y divide-amber-100 bg-amber-50">
                  {record.faulty_parts.map((p, i) => (
                    <li key={i} className="flex justify-between items-start p-2 text-sm">
                      <div>
                        <span className="font-medium">{p.name}</span>
                        <span className="text-neutral-500 font-mono ml-1">× {p.quantity}</span>
                        {p.notes && <div className="text-xs text-neutral-600 mt-0.5 font-mono">{p.notes}</div>}
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {(record.notes || record.return_notes) && (
              <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {record.notes && (<div><div className="label-eyebrow mb-1">Notes</div><div className="text-sm text-neutral-700 border border-neutral-200 p-3">{record.notes}</div></div>)}
                {record.return_notes && (<div><div className="label-eyebrow mb-1">Return Notes</div><div className="text-sm text-neutral-700 border border-neutral-200 p-3">{record.return_notes}</div></div>)}
              </section>
            )}

            {(record.signature || record.photo) && (
              <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {record.signature && (
                  <div>
                    <div className="label-eyebrow mb-1">Signature</div>
                    <img src={record.signature} alt="signature" className="w-full h-40 object-contain border border-neutral-200 bg-white" data-testid="detail-signature" />
                  </div>
                )}
                {record.photo && (
                  <div>
                    <div className="label-eyebrow mb-1">Photo</div>
                    <img src={record.photo} alt="return photo" className="w-full h-48 object-contain border border-neutral-200 bg-white" data-testid="detail-photo" />
                  </div>
                )}
              </section>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t border-neutral-200">
              <Button variant="outline" onClick={onClose} className="rounded-none border-neutral-900 hover:bg-neutral-900 hover:text-white">
                <X size={14} weight="bold" className="mr-2" /> Close
              </Button>
              <Button
                variant="outline"
                onClick={() => downloadReceipt(3)}
                data-testid="download-receipt-3ply-btn"
                className="rounded-none border-neutral-900 hover:bg-neutral-900 hover:text-white"
                title="Original / Office Copy / Technician Copy"
              >
                <FilePdf size={16} weight="bold" className="mr-2" /> Print 3-ply
              </Button>
              <Button onClick={() => downloadReceipt(1)} data-testid="download-receipt-btn" className="rounded-none bg-neutral-900 text-white hover:bg-neutral-800">
                <FilePdf size={16} weight="bold" className="mr-2" /> Download PDF
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, mono = false }) {
  return (
    <div>
      <div className="label-eyebrow">{label}</div>
      <div className={`mt-0.5 ${mono ? "font-mono text-xs" : "font-medium"}`}>{value}</div>
    </div>
  );
}
